/**
 * AdminPoolsPage - Phase 3H-A
 *
 * Admin page for pool management with prop cache controls.
 * BSL Compliant: Factual data only, neutral colors.
 * Added: Delete pool functionality, dark theme fixes
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminPools } from '../../hooks/useAdminPools';
import { AdminNav, AdminPoolTable, CreatePoolForm } from '../../components/admin';
import { Card, CardHeader, CardTitle, CardContent, Button, Select, LoadingSpinner, Alert, Modal } from '../../components/common';
import { AdminPool, AdminCreatePoolRequest } from '../../types';
import { AdminPoolService } from '../../api';

type ViewMode = 'list' | 'create';

interface CacheStatus {
  status: string;
  propCount: number;
  playerCount: number;
  teamCount: number;
  oldestEntry: string | null;
  newestEntry: string | null;
}

export function AdminPoolsPage(): JSX.Element {
  const navigate = useNavigate();
  const {
    pools,
    total,
    loading,
    error,
    page,
    pageSize,
    setPage,
    setFilters,
    refetch,
    createPool,
    deletePool,
    lockPool,
    creating,
    deleting,
    updating,
    actionError,
  } = useAdminPools();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [statusFilter, setStatusFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [lockConfirmPool, setLockConfirmPool] = useState<AdminPool | null>(null);
  const [deleteConfirmPool, setDeleteConfirmPool] = useState<AdminPool | null>(null);
  const [deletingPoolId, setDeletingPoolId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Prop Cache State
  const [cacheStatus, setCacheStatus] = useState<CacheStatus | null>(null);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [cacheError, setCacheError] = useState<string | null>(null);
  const [populatingPool, setPopulatingPool] = useState<string | null>(null);
  const [resolvingPool, setResolvingPool] = useState<string | null>(null);

  // Fetch cache status on mount
  const fetchCacheStatus = useCallback(async () => {
    try {
      const status = await AdminPoolService.getCacheStatus('nfl');
      setCacheStatus(status);
    } catch (err) {
      console.error('Failed to fetch cache status:', err);
    }
  }, []);

  useEffect(() => {
    fetchCacheStatus();
  }, [fetchCacheStatus]);

  const handleFilterChange = () => {
    setFilters({
      status: statusFilter || undefined,
      mode: modeFilter || undefined,
    });
    setPage(1);
  };

  const handleCreatePool = async (data: AdminCreatePoolRequest) => {
    try {
      await createPool(data);
      setViewMode('list');
      setSuccessMessage('Pool created successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch {
      // Error handled by hook
    }
  };

  const handleLockPool = async () => {
    if (!lockConfirmPool) return;
    try {
      await lockPool(lockConfirmPool.pool_id);
      setLockConfirmPool(null);
      setSuccessMessage('Pool locked successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch {
      // Error handled by hook
    }
  };

  const handleDeletePool = async () => {
    if (!deleteConfirmPool) return;
    const poolId = deleteConfirmPool.pool_id;
    setDeletingPoolId(poolId);
    try {
      await deletePool(poolId);
      setDeleteConfirmPool(null);
      setSuccessMessage('Pool deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch {
      // Error handled by hook
    } finally {
      setDeletingPoolId(null);
    }
  };

  const handleEdit = (pool: AdminPool) => {
    navigate(`/admin/pools/${pool.pool_id}`);
  };

  const handleViewDetails = (pool: AdminPool) => {
    navigate(`/admin/pools/${pool.pool_id}`);
  };

  // Prop Cache Handlers
  const handleLoadTestData = async () => {
    setCacheLoading(true);
    setCacheError(null);
    try {
      const result = await AdminPoolService.loadTestData('nfl');
      setSuccessMessage(`Loaded ${result.count} props to cache`);
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchCacheStatus();
    } catch (err) {
      setCacheError(err instanceof Error ? err.message : 'Failed to load test data');
    } finally {
      setCacheLoading(false);
    }
  };

  const handleSyncFromVendor = async () => {
    setCacheLoading(true);
    setCacheError(null);
    try {
      const result = await AdminPoolService.syncFromVendor('nfl');
      setSuccessMessage(result.message || `Synced ${result.count} props from vendor`);
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchCacheStatus();
    } catch (err) {
      setCacheError(err instanceof Error ? err.message : 'Failed to sync from vendor');
    } finally {
      setCacheLoading(false);
    }
  };

  const handlePopulateProps = async (pool: AdminPool) => {
    const poolId = pool.pool_id;
    setPopulatingPool(String(poolId));
    setCacheError(null);
    try {
      const result = await AdminPoolService.populateProps(poolId, pool.domain || 'nfl');
      const propsCreated = result.props_created || 0;
      const propsUpdated = result.props_updated || 0;
      const totalProps = propsCreated + propsUpdated;
      
      if (totalProps === 0) {
        setCacheError(`Warning: No props loaded for ${pool.name || pool.pool_id}. Check events exist for pool date range.`);
      } else {
        setSuccessMessage(`Loaded ${totalProps} props (${propsCreated} new, ${propsUpdated} updated) for ${pool.name || pool.pool_id}`);
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err) {
      setCacheError(err instanceof Error ? err.message : 'Failed to populate props');
    } finally {
      setPopulatingPool(null);
    }
  };

  const handleUpdateOutcomes = async (pool: AdminPool) => {
    const poolId = pool.pool_id;
    setResolvingPool(poolId);
    setCacheError(null);
    try {
      // Step 1: Ingest results from vendor
      await AdminPoolService.ingestResults(poolId);
      
      // Step 2: Resolve pool (grade allocations)
      await AdminPoolService.resolvePool(poolId);
      
      setSuccessMessage(`Outcomes updated for ${pool.name || pool.pool_id}`);
      setTimeout(() => setSuccessMessage(null), 3000);
      refetch();
    } catch (err) {
      setCacheError(err instanceof Error ? err.message : 'Failed to update outcomes');
    } finally {
      setResolvingPool(null);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="flex gap-6 p-6">
      {/* Sidebar */}
      <aside className="w-56 flex-shrink-0">
        <div className="sticky top-6">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
            Admin
          </h2>
          <AdminNav />
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Pool Management</h1>
            <p className="text-slate-400 mt-1">Create, edit, and manage prediction pools</p>
          </div>
          {viewMode === 'list' && (
            <Button variant="primary" onClick={() => setViewMode('create')}>
              Create Pool
            </Button>
          )}
        </div>

        {/* Messages */}
        {error && <Alert variant="error">{error}</Alert>}
        {actionError && <Alert variant="error">{actionError}</Alert>}
        {cacheError && <Alert variant="error">{cacheError}</Alert>}
        {successMessage && <Alert variant="success">{successMessage}</Alert>}

        {/* Prop Cache Management Card */}
        <Card>
          <CardHeader>
            <CardTitle>Prop Cache Management</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              {/* Cache Status */}
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-sm text-slate-400">Cache Status</p>
                  <p className="text-lg font-semibold text-slate-100">
                    {cacheStatus ? (
                      <span className={cacheStatus.propCount > 0 ? 'text-emerald-400' : 'text-amber-400'}>
                        {cacheStatus.propCount} props
                      </span>
                    ) : (
                      <span className="text-slate-500">Loading...</span>
                    )}
                  </p>
                </div>
                {cacheStatus && cacheStatus.propCount > 0 && (
                  <>
                    <div>
                      <p className="text-sm text-slate-400">Players</p>
                      <p className="text-lg font-semibold text-white">{cacheStatus.playerCount ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Teams</p>
                      <p className="text-lg font-semibold text-white">{cacheStatus.teamCount ?? 0}</p>
                    </div>
                  </>
                )}
              </div>

              {/* Cache Actions */}
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={handleLoadTestData}
                  disabled={cacheLoading}
                >
                  {cacheLoading ? 'Loading...' : 'Load Test Data'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleSyncFromVendor}
                  disabled={cacheLoading}
                >
                  {cacheLoading ? 'Syncing...' : 'Sync from Vendor'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchCacheStatus}
                >
                  Refresh
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Create Form */}
        {viewMode === 'create' && (
          <CreatePoolForm
            onSubmit={handleCreatePool}
            onCancel={() => setViewMode('list')}
            loading={creating}
            error={actionError}
          />
        )}

        {/* Pool List */}
        {viewMode === 'list' && (
          <>
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-end gap-4">
                  <div className="w-48">
                    <Select
                      label="Status"
                      value={statusFilter}
                      onChange={setStatusFilter}
                      options={[
                        { value: '', label: 'All Statuses' },
                        { value: 'pending', label: 'Pending' },
                        { value: 'active', label: 'Active' },
                        { value: 'locked', label: 'Locked' },
                        { value: 'completed', label: 'Completed' },
                        { value: 'cancelled', label: 'Cancelled' },
                      ]}
                    />
                  </div>
                  <div className="w-48">
                    <Select
                      label="Mode"
                      value={modeFilter}
                      onChange={setModeFilter}
                      options={[
                        { value: '', label: 'All Modes' },
                        { value: 'pick', label: 'Pick' },
                        { value: 'gm', label: 'GM' },
                      ]}
                    />
                  </div>
                  <Button variant="secondary" onClick={handleFilterChange}>
                    Apply Filters
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Table */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Pools ({total})</CardTitle>
                  <Button variant="ghost" size="sm" onClick={refetch}>
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <LoadingSpinner size="lg" />
                  </div>
                ) : (
                  <AdminPoolTable
                    pools={pools}
                    onEdit={handleEdit}
                    onLock={setLockConfirmPool}
                    onDelete={setDeleteConfirmPool}
                    onViewDetails={handleViewDetails}
                    onPopulateProps={handlePopulateProps}
                    onUpdateOutcomes={handleUpdateOutcomes}
                    populatingPool={populatingPool}
                    resolvingPool={resolvingPool}
                    deletingPool={deletingPoolId}
                  />
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700">
                    <p className="text-sm text-slate-400">
                      Page {page} of {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setPage(page + 1)}
                        disabled={page === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Lock Confirmation Modal */}
        <Modal
          isOpen={!!lockConfirmPool}
          onClose={() => setLockConfirmPool(null)}
          title="Lock Pool"
          size="sm"
        >
          {lockConfirmPool && (
            <div className="space-y-4">
              <p className="text-slate-300">
                Are you sure you want to lock <strong className="text-white">{lockConfirmPool.name || lockConfirmPool.pool_id}</strong>?
              </p>
              <div className="p-3 bg-blue-900/30 border border-blue-700 rounded-lg mb-3">
                <p className="text-sm text-blue-300">
                  <strong>What this does:</strong> Locks all user entries. Users cannot modify rosters after locking.
                </p>
              </div>
              <p className="text-sm text-slate-400">
                This action will be logged in the audit trail.
              </p>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                <Button variant="secondary" onClick={() => setLockConfirmPool(null)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleLockPool} disabled={updating}>
                  {updating ? 'Locking...' : 'Lock Pool'}
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={!!deleteConfirmPool}
          onClose={() => setDeleteConfirmPool(null)}
          title="Delete Pool"
          size="md"
        >
          {deleteConfirmPool && (
            <div className="space-y-4">
              <Alert variant="warning">
                This will cancel the pool and remove all data. This action cannot be undone.
              </Alert>
              
              <div className="p-4 bg-slate-800 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Pool Name:</span>
                    <span className="ml-2 font-medium text-white">{deleteConfirmPool.name || deleteConfirmPool.pool_id}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Pool ID:</span>
                    <span className="ml-2 font-mono text-slate-300">{deleteConfirmPool.pool_id}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Members:</span>
                    <span className="ml-2 text-white">{deleteConfirmPool.member_count ?? 0}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Status:</span>
                    <span className="ml-2 text-white">{deleteConfirmPool.status}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                <Button variant="secondary" onClick={() => setDeleteConfirmPool(null)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleDeletePool}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {deleting ? 'Deleting...' : 'Delete Pool'}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </main>
    </div>
  );
}

export default AdminPoolsPage;
